<script setup lang="ts">
import type { CataloguePart } from "@/types/catalogues.types";

const props = defineProps<{
  part: CataloguePart;
  depth: number;
  selectedPartId: number | null;
  isExpanded: (partId: number) => boolean;
  countDisorders: (part: CataloguePart) => number;
}>();

const emit = defineEmits<{
  (e: "select", partId: number): void;
  (e: "toggle", partId: number): void;
}>();

const paddingLeft = computed(() => 12 + props.depth * 16);
</script>

<template>
  <div class="part-item" :class="{ active: selectedPartId === part.id }">
    <div
      class="part-row"
      :style="{ paddingLeft: `${paddingLeft}px` }"
      @click="emit('select', part.id)"
    >
      <button
        v-if="part.children?.length"
        class="expand-btn"
        @click.stop="emit('toggle', part.id)"
      >
        <el-icon size="12">
          <i
            :class="
              isExpanded(part.id) ? 'bi-chevron-down' : 'bi-chevron-right'
            "
          ></i>
        </el-icon>
      </button>
      <span v-else class="expand-placeholder"></span>
      <span v-if="part.code" class="part-code">{{ part.code }}.</span>
      <span class="part-name">{{ part.name }}</span>
      <span class="disorder-count">{{ countDisorders(part) }}</span>
    </div>
  </div>

  <!-- Recursive children -->
  <template v-if="isExpanded(part.id) && part.children?.length">
    <PartTreeItem
      v-for="child in part.children"
      :key="child.id"
      :part="child"
      :depth="depth + 1"
      :selected-part-id="selectedPartId"
      :is-expanded="isExpanded"
      :count-disorders="countDisorders"
      @select="emit('select', $event)"
      @toggle="emit('toggle', $event)"
    />
  </template>
</template>

<script lang="ts">
import { computed } from "vue";
</script>

<style lang="scss" scoped>
.part-item {
  .part-row {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: background-color 0.15s;
    color: var(--catalogue-text);

    &:hover {
      background-color: var(--catalogue-hover);
    }
  }

  &.active .part-row {
    background-color: var(--catalogue-border);
    color: var(--bs-dark);
    font-weight: 500;
  }
}

.expand-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--catalogue-text);
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.expand-placeholder {
  width: 20px;
  flex-shrink: 0;
}

.part-code {
  color: var(--catalogue-text);
  font-size: 12px;
  flex-shrink: 0;
  opacity: 0.7;
}

.part-name {
  flex: 1;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.disorder-count {
  background-color: var(--catalogue-nav);
  color: var(--catalogue-text);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  flex-shrink: 0;

  .active & {
    background-color: rgba(0, 0, 0, 0.2);
    color: inherit;
  }
}
</style>
